import type { RouteLocationNormalized } from 'vue-router'
import NAV_MAIN from '@/constants/nav/main'
import type { NavMainItem } from '@/types/nav/main'
import { find, flatMap, includes, startsWith } from 'lodash'
import { useCookies } from '@vueuse/integrations/useCookies'
import queryClient from '@/plugins/query'
import { QUERY_KEYS } from '@/constants/query'
import useApi from '@/composables/api'
import NAV_SETTINGS from '@/constants/nav/settings'
import type { NavSettingsItem } from '@/types/nav/settings'

const { api } = useApi()
const cookies = useCookies()

export default async (to: RouteLocationNormalized) => {
    const userHasAccessToken = !!cookies.get(`access_token`)

    // Proceed with paid Breeze check only for authenticated users
    if (!userHasAccessToken) {
        return
    }

    try {
        const allNavItems = [...NAV_MAIN, ...flatMap(NAV_SETTINGS)]

        // Find if any `isPaid: true` nav items match the requested route (or sub-route).
        // Unfortunately we can't use `to.match` because we generally don't use the Vue router for navigation
        const matchingPaidNavItem: NavMainItem | NavSettingsItem | undefined = find(
            allNavItems,
            navItem => !!(navItem.route && navItem.isPaid && startsWith(to.path, navItem.route))
        )

        // If no `isPaid` nav item matches, allow navigation
        if (!matchingPaidNavItem) {
            return
        }

        // Else fetch `me` data since we're requesting access to a paid feature
        const { data: meData } = await queryClient.ensureQueryData({
            queryKey: QUERY_KEYS.me,
            queryFn: () => api.authorizationLoggedInUser.getMe()
        })

        const userIsAdmin = meData.is_primary || includes(meData.roles, `tithely:administrator`)

        // If paid is required
        if (!meData.organization.paid) {
            if (userIsAdmin) {
                window.location.href = `/account/tithely/sso?redirect=/offer/breeze`
            } else {
                return {
                    path: `/errors/freebreeze`,
                    query: {
                        previous: encodeURIComponent(window.location.href)
                    }
                }
            }
        }

        // If 3.0 is or is not required
        if (
            matchingPaidNavItem.hasTithelyUi !== undefined &&
            matchingPaidNavItem.hasTithelyUi !== meData.organization.has_tithely_ui
        ) {
            return
        }
    } catch (error) {
        return
    }
}
